import { useEffect, useRef, useState } from 'react'
import { REGIMES_TRIBUTARIOS } from '../../db/tipos'
import { buscarCnpj } from '../../representadas/buscarCnpj'
import { camposDoCnpj, type FormularioRepresentada } from '../../representadas/formularioRepresentada'
import { ICMS_INTERNO_POR_UF } from '../../impostos/icmsInternoPorUf'
import { ESTILO_CAMPO, ESTILO_ROTULO, type PropsAba } from './estilosDaFornecedora'
import SecaoCadastro, { ComIcone } from './SecaoCadastro'
import { IconePredio } from '../produtos/IconesDaFicha'
import { IconeAnotacao, IconeEmail, IconeInstagram, IconeLink, IconePessoa, IconeLupa, IconeTelefone, IconeWhats } from './IconesDoCadastro'

// As 27 siglas ja testadas em icmsInternoPorUf.ts (0024), sem duplicar a lista.
const UFS = Object.keys(ICMS_INTERNO_POR_UF).sort()

export default function AbaIdentificacao({ form, setForm, editavel }: PropsAba) {
  const [buscando, setBuscando] = useState(false)
  const [aviso, setAviso] = useState<string | null>(null)
  // F2 (review final): `editavel` so era checado ANTES do await -- se o
  // Salvar ou o Descartar fechassem a edicao enquanto a BrasilAPI ainda
  // respondia, o resultado atrasado preenchia um formulario ja gravado ou
  // descartado. O ref reflete `editavel` no instante em que a resposta chega.
  const editavelRef = useRef(editavel)
  useEffect(() => { editavelRef.current = editavel }, [editavel])
  const mudar = (p: Partial<FormularioRepresentada>) => setForm((f) => ({ ...f, ...p }))

  // BrasilAPI (spec 17/09): preenche so o que esta vazio; falha so avisa.
  async function aoSairDoCnpj() {
    if (!editavel || form.cnpj.trim() === '') { setAviso(null); return }
    setBuscando(true)
    setAviso(null)
    const encontrado = await buscarCnpj(form.cnpj)
    setBuscando(false)
    if (!editavelRef.current) return // aba nao esta mais em edicao: descarta o resultado atrasado
    if (!encontrado) { setAviso('Não consegui buscar esse CNPJ — preencha manualmente.'); return }
    setForm((f) => ({ ...f, ...camposDoCnpj(f, encontrado) }))
  }

  // Link para o WhatsApp (spec 17/09), agora do campo proprio do WhatsApp.
  const numeroWhats = form.whatsapp.replace(/\D/g, '')
  const linkWhats = numeroWhats.length >= 10 ? `https://wa.me/${numeroWhats.length <= 11 ? '55' : ''}${numeroWhats}` : null
  const campoComIcone = `${ESTILO_CAMPO} pl-9`

  // Pedido dela (28/09): 3 cards -- Dados da empresa, Canais de contato (que
  // veio da Localizacao) e Observacoes.
  return (
    <div className="space-y-3">
      <SecaoCadastro icone={<IconePredio />} titulo="Dados da empresa">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="nome" className={ESTILO_ROTULO}>Nome fantasia</label>
            <input id="nome" required value={form.nome} onChange={(e) => mudar({ nome: e.target.value })}
              disabled={!editavel} className={ESTILO_CAMPO} />
          </div>
          <div>
            <label htmlFor="cnpj" className={ESTILO_ROTULO}>CNPJ</label>
            <input id="cnpj" inputMode="numeric" value={form.cnpj} onChange={(e) => mudar({ cnpj: e.target.value })}
              onBlur={() => void aoSairDoCnpj()} disabled={!editavel} className={ESTILO_CAMPO} />
          </div>
        </div>
        {/* Botao visivel (pedido dela, 28/09): a busca ja rodava ao sair do
            campo, mas ninguem sabia que existia. */}
        {editavel && (
          <button type="button" onClick={() => void aoSairDoCnpj()} disabled={buscando}
            className="flex w-full items-center justify-center gap-1 rounded-lg bg-teal-50 py-2 text-sm font-medium text-teal-800">
            <IconeLupa tamanho={16} /> Buscar dados pelo CNPJ
          </button>
        )}
        {buscando && <p className="text-xs text-slate-500">Buscando dados do CNPJ…</p>}
        {aviso && <p className="text-xs text-amber-700">{aviso}</p>}
        <div>
          <label htmlFor="razao" className={ESTILO_ROTULO}>Razão social</label>
          <input id="razao" value={form.razaoSocial} onChange={(e) => mudar({ razaoSocial: e.target.value })}
            disabled={!editavel} className={ESTILO_CAMPO} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="ie" className={ESTILO_ROTULO}>Inscrição estadual</label>
            <input id="ie" value={form.inscricaoEstadual} onChange={(e) => mudar({ inscricaoEstadual: e.target.value })}
              placeholder="ISENTO" disabled={!editavel} className={ESTILO_CAMPO} />
          </div>
          <div>
            <label htmlFor="im" className={ESTILO_ROTULO}>Inscrição municipal</label>
            <input id="im" value={form.inscricaoMunicipal} onChange={(e) => mudar({ inscricaoMunicipal: e.target.value })}
              disabled={!editavel} className={ESTILO_CAMPO} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="regime" className={ESTILO_ROTULO}>Regime tributário</label>
            <select id="regime" value={form.regimeTributario} onChange={(e) => mudar({ regimeTributario: e.target.value })}
              disabled={!editavel} className={ESTILO_CAMPO}>
              <option value="">— Selecione —</option>
              {REGIMES_TRIBUTARIOS.map((r) => <option key={r.codigo} value={r.codigo}>{r.rotulo}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="segmento" className={ESTILO_ROTULO}>Segmento</label>
            <input id="segmento" value={form.segmento} onChange={(e) => mudar({ segmento: e.target.value })}
              placeholder="Ex.: Alimentos" disabled={!editavel} className={ESTILO_CAMPO} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="prazo" className={ESTILO_ROTULO}>Prazo de entrega (dias)</label>
            <input id="prazo" type="number" min="0" value={form.prazoMedio} onChange={(e) => mudar({ prazoMedio: e.target.value })}
              disabled={!editavel} className={ESTILO_CAMPO} />
          </div>
          <div>
            <label htmlFor="transp" className={ESTILO_ROTULO}>Transportadoras</label>
            <input id="transp" value={form.transportadoras} onChange={(e) => mudar({ transportadoras: e.target.value })}
              placeholder="Separadas por vírgula" disabled={!editavel} className={ESTILO_CAMPO} />
          </div>
        </div>
        <div className="rounded-xl bg-teal-50/70 p-3 space-y-2">
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={form.calculaImposto} className="mt-1"
              onChange={(e) => mudar({ calculaImposto: e.target.checked })} disabled={!editavel} />
            <span>
              <span className="block text-sm font-semibold text-slate-800">Esta representada manda alíquota</span>
              <span className="block text-xs text-slate-600">O app mostra o imposto estimado no pedido, conforme a alíquota da representada. O preço cobrado continua o da tabela.</span>
            </span>
          </label>
          {form.calculaImposto && (
            <div>
              <label htmlFor="uf-origem" className="block text-xs text-slate-600 mb-1">UF de origem (planta da fábrica)</label>
              <select id="uf-origem" value={form.ufOrigem} onChange={(e) => mudar({ ufOrigem: e.target.value })}
                disabled={!editavel} className={ESTILO_CAMPO}>
                <option value="">Selecione…</option>
                {UFS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
              </select>
            </div>
          )}
        </div>
      </SecaoCadastro>

      <SecaoCadastro icone={<IconeTelefone />} titulo="Canais de contato">
        <div>
          <label htmlFor="rep-tel" className={ESTILO_ROTULO}>Telefone comercial</label>
          <div className="flex gap-2">
            <div className="w-16 shrink-0">
              <input aria-label="DDD" value={form.ddd} onChange={(e) => mudar({ ddd: e.target.value })}
                placeholder="DDD" inputMode="numeric" maxLength={3} disabled={!editavel} className={ESTILO_CAMPO} />
            </div>
            <div className="min-w-0 flex-1">
              <ComIcone icone={<IconeTelefone tamanho={16} />}>
                <input id="rep-tel" inputMode="tel" value={form.telefone} onChange={(e) => mudar({ telefone: e.target.value })}
                  disabled={!editavel} className={campoComIcone} />
              </ComIcone>
            </div>
          </div>
        </div>
        <div>
          <label htmlFor="rep-whats" className={ESTILO_ROTULO}>WhatsApp</label>
          <div className="flex gap-2 items-center">
            <div className="min-w-0 flex-1">
              <ComIcone icone={<IconeWhats tamanho={16} />}>
                <input id="rep-whats" inputMode="tel" value={form.whatsapp} onChange={(e) => mudar({ whatsapp: e.target.value })}
                  placeholder="(21) 99999-0000" disabled={!editavel} className={campoComIcone} />
              </ComIcone>
            </div>
            {linkWhats && (
              <a href={linkWhats} target="_blank" rel="noreferrer" className="shrink-0 text-emerald-700 text-sm underline">Abrir</a>
            )}
          </div>
        </div>
        <div>
          <label htmlFor="rep-email" className={ESTILO_ROTULO}>E-mail comercial</label>
          <ComIcone icone={<IconeEmail tamanho={16} />}>
            <input id="rep-email" type="email" value={form.email} onChange={(e) => mudar({ email: e.target.value })}
              disabled={!editavel} className={campoComIcone} />
          </ComIcone>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="rep-site" className={ESTILO_ROTULO}>Site</label>
            <ComIcone icone={<IconeLink tamanho={16} />}>
              <input id="rep-site" value={form.website} onChange={(e) => mudar({ website: e.target.value })}
                disabled={!editavel} className={campoComIcone} />
            </ComIcone>
          </div>
          <div>
            <label htmlFor="rep-insta" className={ESTILO_ROTULO}>Instagram</label>
            <ComIcone icone={<IconeInstagram tamanho={16} />}>
              <input id="rep-insta" value={form.instagram} onChange={(e) => mudar({ instagram: e.target.value })}
                placeholder="@perfil" disabled={!editavel} className={campoComIcone} />
            </ComIcone>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="rep-contato" className={ESTILO_ROTULO}>Pessoa de contato</label>
            <ComIcone icone={<IconePessoa tamanho={16} />}>
              <input id="rep-contato" value={form.nomeContato} onChange={(e) => mudar({ nomeContato: e.target.value })}
                disabled={!editavel} className={campoComIcone} />
            </ComIcone>
          </div>
          <div>
            <label htmlFor="rep-cargo" className={ESTILO_ROTULO}>Cargo</label>
            <input id="rep-cargo" value={form.cargo} onChange={(e) => mudar({ cargo: e.target.value })}
              disabled={!editavel} className={ESTILO_CAMPO} />
          </div>
        </div>
      </SecaoCadastro>

      <SecaoCadastro icone={<IconeAnotacao />} titulo="Observações">
        <div>
          <label htmlFor="obs" className="sr-only">Observações</label>
          <textarea id="obs" value={form.observacoes} onChange={(e) => mudar({ observacoes: e.target.value })}
            maxLength={500} rows={4} placeholder="Ex.: horário de funcionamento, informações importantes"
            disabled={!editavel} className={`${ESTILO_CAMPO} min-h-24 max-h-48 overflow-y-auto resize-none`} />
          <p className="text-right text-[11px] text-slate-400">{form.observacoes.length}/500</p>
        </div>
      </SecaoCadastro>
    </div>
  )
}
